// Components/Services/EditCustomCode.jsx
import React, { useEffect, useRef, useState } from "react";
import { Card, Button, Form, Row, Col, Alert } from "react-bootstrap";
import { FaUpload, FaTimes } from "react-icons/fa";
import { AddServicesPricing } from "./AddServicesPricing";
import { getServiceById, updateService, getPractice } from "../../api";
import { jwtDecode } from "jwt-decode";
import "./Services.css";

export function EditCustomCodeInline({ serviceId, onServiceUpdate = () => {}, onHide = () => {} }) {
  const successRef = useRef(null);
  const [pracName, setPracName] = useState([]);
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [servicesName, setServicesName] = useState("");
  const [servicePricing, setServicePricing] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [validatePricingFn, setValidatePricingFn] = useState(() => () => true);
  const [pricingErrors, setPricingErrors] = useState({});
  const [errors, setErrors] = useState({});
  const [successAlert, setSuccessAlert] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);
  const [user, setUser] = useState({});

  useEffect(() => {
    async function load() {
      const token = sessionStorage.getItem("user");
      if (token) {
        const decoded = jwtDecode(token);
        setUser(decoded);
        const practices = await getPractice();
        const userPractices = (practices || []).filter((p) =>
          decoded.practice?.includes(p.practiceNumber)
        );
        setPracName(userPractices);
      }

      if (serviceId) {
        const svc = await getServiceById(serviceId);
        if (svc) {
          setServicesName(svc.servicesName || "");
          setSelectedPractices(
            Array.isArray(svc.servicesPractice) ? svc.servicesPractice : []
          );
          setServicePricing(svc.pricing || {});
        }
      }
      setIsLoaded(true);
    }
    load();
  }, [serviceId]);

  const validateBase = () => {
    const next = {};
    if (!servicesName.trim()) next.servicesName = "Code is required.";
    if (!selectedPractices.length) next.servicesPractice = "Select at least one practice.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handlePracticeChange = (practiceNumber) => {
    setSelectedPractices((prev) =>
      prev.includes(practiceNumber)
        ? prev.filter((p) => p !== practiceNumber)
        : [...prev, practiceNumber]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    const baseOk = validateBase();
    const pricingOk =
      typeof validatePricingFn === "function" ? validatePricingFn() : true;
    if (!baseOk || !pricingOk) return;

    try {
      const resp = await updateService(serviceId, {
        serviceType: "custom_code",
        servicesName: servicesName.trim(),
        servicesPractice: selectedPractices,
        // Pass paymentOptions through as-is — the user's flatRate vs
        // flatRate+medicalAidRate choice is honoured for custom codes too.
        pricing: pricing || servicePricing || {},
      });

      if (resp?.status === 200) {
        onServiceUpdate();
        setSuccessAlert(true);
        requestAnimationFrame(() => {
          successRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
        setTimeout(() => {
          setSuccessAlert(false);
          onHide();
        }, 700);
      }
    } catch (err) {
      console.error("Error updating custom code:", err);
      const msg =
        err?.response?.data?.message ||
        "Failed to update custom code. Please try again.";
      setSubmitError(msg);
    }
  };

  if (!isLoaded) {
    return (
      <Card className="shadow-sm border-0 mb-3">
        <Card.Body className="pt-2">
          <p className="text-muted">Loading custom code…</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm border-0 mb-3">
      <Card.Body className="pt-2">
        {successAlert && (
          <Alert ref={successRef} variant="success" className="w-100 fs-6 fw-normal py-2">
            The custom code was updated successfully!
          </Alert>
        )}

        {submitError && (
          <Alert
            variant="danger"
            className="w-100 fs-6 fw-normal py-2"
            dismissible
            onClose={() => setSubmitError("")}
          >
            {submitError}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <h6 className="text-primary">Edit Custom Code</h6>

          <Row className="mb-3">
            <Form.Group as={Col} md={4} controlId="editCustomCodeName">
              <Form.Label>Code</Form.Label>
              <Form.Control
                value={servicesName}
                isInvalid={!!errors.servicesName}
                placeholder="e.g. X-RAY-CHEST"
                onChange={(e) => setServicesName(e.target.value)}
                maxLength={60}
              />
              <Form.Control.Feedback type="invalid">{errors.servicesName}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md={8} controlId="editCustomCodePractices">
              <Form.Label>Select Practices</Form.Label>
              <div>
                {pracName.map((prac, index) => (
                  <Form.Check
                    key={prac.practiceNumber || `practice-${index}`}
                    type="checkbox"
                    label={prac.practiceName || "Unknown Practice"}
                    checked={selectedPractices.includes(prac.practiceNumber)}
                    onChange={() => handlePracticeChange(prac.practiceNumber)}
                  />
                ))}
              </div>
              {errors.servicesPractice && (
                <div className="text-danger">{errors.servicesPractice}</div>
              )}
            </Form.Group>
          </Row>

          <hr />
          <h6 className="text-primary">Billing</h6>
          <AddServicesPricing
            key={serviceId}
            onPricingChange={setPricing}
            servicePricing={servicePricing}
            selectedPractices={selectedPractices}
            pracName={pracName}
            user={user}
            onValidate={(fn) => setValidatePricingFn(() => fn)}
            onErrorsChange={setPricingErrors}
          />
          {Object.keys(pricingErrors).length > 0 && (
            <div className="text-danger small mt-2">
              Please fix the billing errors above.
            </div>
          )}

          <div className="d-flex gap-2 mt-3">
            <Button variant="primary" className="px-5 py-2" type="submit">
              <FaUpload className="me-2" /> Save Changes
            </Button>
            <Button variant="outline-secondary" className="px-5 py-2" type="button" onClick={onHide}>
              <FaTimes className="me-2" /> Cancel
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}

export default EditCustomCodeInline;
